import * as React from 'react';

/**
 * Allow undo/redo based on list of changes
 */
export function useUndoChanges<T>(
  initialChanges: T[],
  /** number of changes that are protected from being undone
   *
   * defaults to 0
   */
  protectedChanges: number = 0,
) {
  type State = { changes: T[]; visibleLength: number };

  const [data, setData] = React.useState<State>(
    (): State => ({ changes: initialChanges, visibleLength: initialChanges.length }),
  );

  const { set, undo, redo } = React.useMemo(
    () => ({
      set: (newChanges: T[]) => {
        setData({
          changes: newChanges,
          visibleLength: newChanges.length, // removes previous redo
        });
      },
      undo: () => setData((old) => ({ ...old, visibleLength: Math.max(protectedChanges, old.visibleLength - 1) })),
      redo: () => setData((old) => ({ ...old, visibleLength: Math.min(old.changes.length, old.visibleLength + 1) })),
    }),
    [protectedChanges],
  );

  const { changes, visibleLength } = data;
  const { visibleChanges, ...info } = React.useMemo(() => {
    const visibleChanges = changes.slice(0, visibleLength);
    console.assert(visibleChanges.length === visibleLength);

    const undoSize = visibleLength - protectedChanges;
    const redoSize = changes.length - visibleLength;

    const canUndo = undoSize > 0;
    const canRedo = redoSize > 0;
    return { visibleChanges, undoSize, canUndo, redoSize, canRedo };
  }, [changes, visibleLength, protectedChanges]);

  return [
    visibleChanges,
    set,
    {
      undo: info.canUndo ? undo : undefined,
      redo: info.canRedo ? redo : undefined,
      ...info,
    },
  ] as const;
}
